import { attachmentFilename } from '#lib/attachmentFilename';
import { fetcher } from '#lib/backendFetcher';

const BASE = '/api/v1/custom-columns';

/**
 * @returns {Promise<Object[]>} Org columns, each with `fill` (state of its latest fill run)
 */
export const fetchCustomColumns = async () => (await fetcher(BASE))?.columns ?? [];

/**
 * @param {{name: string, type: 'ai'|'formula', prompt?: string, template?: string, fallback?: string, useWebsite?: boolean}} body
 * @returns {Promise<Object>} The created column (with its generated `key`)
 */
export const createCustomColumn = (body) => fetcher(BASE, { method: 'POST', body });

/**
 * @param {{id: string, updates: Object}} params
 * @returns {Promise<Object>}
 */
export const updateCustomColumn = ({ id, updates }) => fetcher(`${BASE}/${id}`, { method: 'PATCH', body: updates });

/**
 * @param {string} id
 * @returns {Promise<void>}
 */
export const deleteCustomColumn = (id) => fetcher(`${BASE}/${id}`, { method: 'DELETE' });

/**
 * Tests an (unsaved) definition on up to five rows; nothing is stored.
 * @param {Object} body - `{ type, prompt|template, fallback, useWebsite, scope }`
 * @returns {Promise<{rows: Object[]}>}
 */
export const previewCustomColumn = (body) => fetcher(`${BASE}/preview`, { method: 'POST', body });

/**
 * @param {{id: string, scope: Object, mode: 'missing'|'all', overwriteEdited?: boolean}} params
 * @returns {Promise<{jobId: string, total: number}>}
 */
export const startColumnFill = ({ id, scope, mode, overwriteEdited = false }) =>
	fetcher(`${BASE}/${id}/fill`, { method: 'POST', body: { scope, mode, overwriteEdited } });

/**
 * Hand-edits an ai cell; an empty value clears it.
 * @param {{id: string, crmContactId?: string, crmCompanyId?: string, value: string}} params
 * @returns {Promise<{value: string|null}>}
 */
export const setCustomCellValue = ({ id, ...body }) => fetcher(`${BASE}/${id}/values`, { method: 'PUT', body });

/**
 * CSV of one contact-list tab with standard and custom columns.
 * @param {string} listId
 * @param {{tab: 'contacts'|'companies', columns: string[], customColumnIds: string[], scope?: Object}} body
 * @returns {Promise<{blob: Blob, filename: string}>}
 */
export const exportContactListCsv = async (listId, body) => {
	const response = await fetcher(`/api/v1/contact-lists/${listId}/export-csv`, {
		method: 'POST',
		body,
		responseType: 'stream',
	});
	const filename = attachmentFilename(response.headers.get('Content-Disposition'), 'kontaktliste_export.csv');
	return { blob: await response.blob(), filename };
};
